import { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import toast from 'react-hot-toast';

export type JobStatus = 'running' | 'completed' | 'failed';

export interface TrackedJob {
  id: string;
  type: string;
  label: string;
  status: JobStatus;
  startedAt: string;
  completedAt?: string;
  error?: string;
  pollFn?: () => Promise<{ done: boolean; success?: boolean; error?: string }>;
  pollIntervalMs?: number;
}

interface JobTrackerContextType {
  jobs: TrackedJob[];
  activeCount: number;
  addJob: (job: Omit<TrackedJob, 'status' | 'startedAt'>) => string;
  updateJob: (id: string, updates: Partial<TrackedJob>) => void;
  removeJob: (id: string) => void;
  clearCompleted: () => void;
}

const JobTrackerContext = createContext<JobTrackerContextType | null>(null);

export function useJobTracker() {
  const ctx = useContext(JobTrackerContext);
  if (!ctx) throw new Error('useJobTracker must be used within JobTrackerProvider');
  return ctx;
}

export function JobTrackerProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<TrackedJob[]>([]);
  const pollTimers = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  const activeCount = jobs.filter(j => j.status === 'running').length;

  const addJob = useCallback((job: Omit<TrackedJob, 'status' | 'startedAt'>) => {
    const id = job.id || crypto.randomUUID();
    const newJob: TrackedJob = {
      ...job,
      id,
      status: 'running',
      startedAt: new Date().toISOString(),
    };

    setJobs(prev => [...prev, newJob]);

    // Set up polling if pollFn provided
    if (job.pollFn) {
      const interval = job.pollIntervalMs || 10000;
      let attempts = 0;
      const maxAttempts = 36; // 6 min at 10s

      const timer = setInterval(async () => {
        attempts++;
        try {
          const result = await job.pollFn!();
          if (result.done) {
            clearInterval(timer);
            pollTimers.current.delete(id);
            setJobs(prev => prev.map(j => j.id === id ? {
              ...j,
              status: result.success ? 'completed' : 'failed',
              completedAt: new Date().toISOString(),
              error: result.error,
            } : j));

            if (result.success) {
              toast.success(`${job.label} completed`, { duration: 5000 });
            } else {
              toast.error(`${job.label} failed: ${result.error || 'Unknown error'}`, { duration: 8000 });
            }
          } else if (attempts >= maxAttempts) {
            clearInterval(timer);
            pollTimers.current.delete(id);
            setJobs(prev => prev.map(j => j.id === id ? {
              ...j,
              status: 'failed',
              completedAt: new Date().toISOString(),
              error: 'Timed out',
            } : j));
            toast.error(`${job.label} timed out`, { duration: 8000 });
          }
        } catch {
          // Silent retry
        }
      }, interval);

      pollTimers.current.set(id, timer);
    }

    return id;
  }, []);

  const updateJob = useCallback((id: string, updates: Partial<TrackedJob>) => {
    setJobs(prev => prev.map(j => j.id === id ? { ...j, ...updates } : j));

    // If job is being marked complete/failed, stop polling and show toast
    if (updates.status === 'completed' || updates.status === 'failed') {
      const timer = pollTimers.current.get(id);
      if (timer) {
        clearInterval(timer);
        pollTimers.current.delete(id);
      }
    }
  }, []);

  const removeJob = useCallback((id: string) => {
    const timer = pollTimers.current.get(id);
    if (timer) {
      clearInterval(timer);
      pollTimers.current.delete(id);
    }
    setJobs(prev => prev.filter(j => j.id !== id));
  }, []);

  const clearCompleted = useCallback(() => {
    setJobs(prev => prev.filter(j => j.status === 'running'));
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      pollTimers.current.forEach(timer => clearInterval(timer));
    };
  }, []);

  return (
    <JobTrackerContext.Provider value={{ jobs, activeCount, addJob, updateJob, removeJob, clearCompleted }}>
      {children}
    </JobTrackerContext.Provider>
  );
}
